import React, { useState, useEffect } from 'react';
import { getCalls } from '../../../services/mysteryShopService';
import type { MysteryShopCall } from '../../../types/mysteryShop';
import { ViewToolbar } from '../shared';
import { getMonthName, getMonthDateRange } from '../utils';
import type { PropertyStat } from './property-performance';
import { WidgetScore } from './widget-score';
import { WidgetTour } from './widget-tour';
import { WidgetCalls } from './widget-calls';
import { PropertyPerformance } from './property-performance';
import { RecentCalls } from './recent-calls';

const MAX_PROPERTIES = 9;
const MAX_RECENT_CALLS = 7;

interface DashboardViewProps {
  onSelectProperty: (id: string | null) => void;
  onSelectCall: (id: string) => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({ onSelectProperty, onSelectCall }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [calls, setCalls] = useState<MysteryShopCall[]>([]);
  const [prevMonthCalls, setPrevMonthCalls] = useState<MysteryShopCall[]>([]);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    const { startDate, endDate } = getMonthDateRange(selectedMonth);
    const prevMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1);
    const { startDate: prevStart, endDate: prevEnd } = getMonthDateRange(prevMonth);
    Promise.all([
      getCalls({}, { startDate, endDate }, 'property'),
      getCalls({}, { startDate: prevStart, endDate: prevEnd })
    ]).then(([current, prev]) => {
      setCalls(current);
      setPrevMonthCalls(prev);
    });
  }, [selectedMonth, rev]);

  // Derived values
  const avgScore = calls.length > 0
    ? calls.reduce((sum, c) => sum + c.overallScore, 0) / calls.length
    : 0;
  const lastAvgScore = prevMonthCalls.length > 0
    ? prevMonthCalls.reduce((sum, c) => sum + c.overallScore, 0) / prevMonthCalls.length
    : 0;
  const tourRate = calls.length > 0
    ? (calls.filter(c => c.tourInvited).length / calls.length) * 100
    : 0;
  const lastTourRate = prevMonthCalls.length > 0
    ? (prevMonthCalls.filter(c => c.tourInvited).length / prevMonthCalls.length) * 100
    : 0;
  const distinctPropertyCount = new Set(calls.map(c => c.propertyId)).size;

  const propertyStats: PropertyStat[] = (() => {
    const propMap = new Map<string, { name: string; calls: MysteryShopCall[]; prevCalls: MysteryShopCall[] }>();
    calls.forEach(call => {
      if (!propMap.has(call.propertyId)) {
        propMap.set(call.propertyId, { name: call.propertyName, calls: [], prevCalls: [] });
      }
      propMap.get(call.propertyId)!.calls.push(call);
    });
    prevMonthCalls.forEach(call => {
      if (propMap.has(call.propertyId)) {
        propMap.get(call.propertyId)!.prevCalls.push(call);
      }
    });
    return Array.from(propMap.entries()).map(([propertyId, { name, calls: propCalls, prevCalls }]) => {
      const avg = propCalls.length > 0
        ? propCalls.reduce((s, c) => s + c.overallScore, 0) / propCalls.length
        : null;
      const prevAvg = prevCalls.length > 0
        ? prevCalls.reduce((s, c) => s + c.overallScore, 0) / prevCalls.length
        : null;
      const tour = propCalls.length > 0
        ? (propCalls.filter(c => c.tourInvited).length / propCalls.length) * 100
        : null;
      return {
        id: propertyId, name,
        callCount: propCalls.length,
        avgScore: avg, tourRate: tour,
        trend: avg !== null && prevAvg !== null ? avg - prevAvg : null
      };
    }).sort((a, b) => (b.avgScore ?? 0) - (a.avgScore ?? 0));
  })();

  const recentCalls = [...calls]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, MAX_RECENT_CALLS);

  const isCurrentMonth =
    selectedMonth.getMonth() === new Date().getMonth() &&
    selectedMonth.getFullYear() === new Date().getFullYear();

  return (
    <div className="space-y-8">
      <ViewToolbar onRefresh={() => setRev(r => r + 1)}>
        <button
          onClick={() => {
            const prev = new Date(selectedMonth);
            prev.setMonth(prev.getMonth() - 1);
            setSelectedMonth(prev);
          }}
          className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
        >
          ← Previous
        </button>
        <span className="px-4 py-1.5 bg-white rounded-lg border border-gray-200 text-sm font-medium">
          {getMonthName(selectedMonth)}
        </span>
        <button
          onClick={() => {
            const next = new Date(selectedMonth);
            next.setMonth(next.getMonth() + 1);
            if (next <= new Date()) setSelectedMonth(next);
          }}
          disabled={isCurrentMonth}
          className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next →
        </button>
      </ViewToolbar>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Portfolio Overview</h1>
        <p className="text-gray-500 mt-1">{getMonthName(selectedMonth)}</p>
      </div>

      <div className="grid grid-cols-4 gap-4" data-tour="mystery-shop-portfolio-snapshot">
        <WidgetScore avgScore={avgScore} trend={avgScore - lastAvgScore} />
        <WidgetTour tourRate={tourRate} trend={tourRate - lastTourRate} />
        <WidgetCalls
          callCount={calls.length}
          distinctPropertyCount={distinctPropertyCount}
          propertiesBelowThreshold={propertyStats.filter(p => p.avgScore !== null && p.avgScore < 70).length}
        />
      </div>

      <div className="grid grid-cols-3 gap-6">
        <PropertyPerformance
          propertyStats={propertyStats}
          maxRows={MAX_PROPERTIES}
          onSelectProperty={onSelectProperty}
        />
        <RecentCalls calls={recentCalls} onSelectCall={onSelectCall} />
      </div>
    </div>
  );
};
